import { useEffect, useId, useState } from 'react';
import { isAxiosError } from 'axios';
import { Check, Loader2 } from 'lucide-react';
import Layout from '../layout/Layout';
import scholarshipGrantService, {
  type ScholarshipEnglishCertificateType,
  type ScholarshipEnglishForm,
} from '../services/scholarshipGrant.service';
import { extractErrorMessage } from '../components/scholarshipApplication/validation';
import { useParams } from 'react-router-dom';

const SCORE_PRESETS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

const CERTIFICATES: Array<{ id: ScholarshipEnglishCertificateType; title: string; latin?: boolean; hint?: string }> = [
  { id: 'moi', title: 'بكالوريوس دراسته بالإنجليزي', latin: false, hint: 'MOI' },
  { id: 'ielts', title: 'IELTS', latin: true },
  { id: 'toefl', title: 'TOEFL', latin: true },
  { id: 'other', title: 'شهادة تانية' },
  { id: 'none', title: 'معنديش شهادة لغة إنجليزي' },
];

type ScoreChoice = (typeof SCORE_PRESETS)[number] | 'custom' | '';

interface FieldErrors {
  certificate?: string;
  otherName?: string;
  score?: string;
  issuedOn?: string;
}

function cleanServerMessage(detail: unknown, fallback: string) {
  return extractErrorMessage(detail, fallback).replace(/^Value error,\s*/i, '');
}

function hydrateScore(score: string | null): { choice: ScoreChoice; custom: string } {
  if (!score) return { choice: '', custom: '' };
  if ((SCORE_PRESETS as readonly string[]).includes(score)) return { choice: score as ScoreChoice, custom: '' };
  return { choice: 'custom', custom: score };
}

export default function ScholarshipEnglish() {
  const { token = '' } = useParams();
  const formId = useId();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [profile, setProfile] = useState<ScholarshipEnglishForm | null>(null);
  const [certificate, setCertificate] = useState<ScholarshipEnglishCertificateType | ''>('');
  const [otherName, setOtherName] = useState('');
  const [scoreChoice, setScoreChoice] = useState<ScoreChoice>('');
  const [customScore, setCustomScore] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    document.title = 'بيانات شهادة اللغة الإنجليزية | EduNasser';
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    scholarshipGrantService.getEnglishForm(token)
      .then((data) => {
        if (cancelled) return;
        const score = hydrateScore(data.score);
        setProfile(data);
        setCertificate(data.certificate_type ?? '');
        setOtherName(data.certificate_other ?? '');
        setScoreChoice(score.choice);
        setCustomScore(score.custom);
        setIssuedOn(data.issued_on ?? '');
        setSaved(Boolean(data.submitted_at));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isAxiosError(error) && error.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  const skipsDetails = certificate === 'moi' || certificate === 'none';
  const needsDetails = certificate !== '' && !skipsDetails;

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!certificate) next.certificate = 'اختار نوع الشهادة';
    if (certificate === 'other') {
      const name = otherName.trim();
      if (name.length < 2 || name.length > 120) next.otherName = 'اكتب اسم الشهادة، بين حرفين و120 حرفًا';
    }
    if (needsDetails) {
      if (!scoreChoice) next.score = 'اختار المستوى أو اكتبه';
      else if (scoreChoice === 'custom' && !customScore.trim()) next.score = 'اكتب المستوى';
      else if (scoreChoice === 'custom' && customScore.trim().length > 40) next.score = 'المستوى يجب ألا يتجاوز 40 حرفًا';
      if (!issuedOn) next.issuedOn = 'اختار تاريخ إصدار الشهادة';
    }
    return next;
  };

  const save = async () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    setSaveError(null);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      document.getElementById(`${formId}-${firstInvalid}`)?.focus();
      return;
    }
    if (!certificate) return;
    setSaving(true);
    try {
      const savedForm = await scholarshipGrantService.saveEnglishForm(token, {
        certificate_type: certificate,
        certificate_other: certificate === 'other' ? otherName.trim() : null,
        score: skipsDetails ? null : (scoreChoice === 'custom' ? customScore.trim() : scoreChoice),
        issued_on: skipsDetails ? null : issuedOn,
      });
      const score = hydrateScore(savedForm.score);
      setProfile(savedForm);
      setCertificate(savedForm.certificate_type ?? certificate);
      setOtherName(savedForm.certificate_other ?? '');
      setScoreChoice(score.choice);
      setCustomScore(score.custom);
      setIssuedOn(savedForm.issued_on ?? '');
      setSaved(true);
    } catch (error: unknown) {
      const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
      setSaveError(cleanServerMessage(detail, 'مقدرناش نحفظ البيانات. اللي كتبته لسه موجود، جرّب تاني.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div dir="rtl" lang="ar" className="min-h-screen bg-[#FAF9FD] px-4 pb-16 pt-28 text-[#2C2C2C] sm:px-6">
        <div className="mx-auto max-w-xl">
          {loading ? (
            <div className="rounded-3xl border border-[#E6E1F2] bg-white p-8 shadow-sm" role="status">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#5B3FD0]" />
              <p className="mt-3 text-center text-sm font-semibold text-[#707070]">بنحمّل بياناتك</p>
            </div>
          ) : notFound ? (
            <div className="rounded-3xl border border-[#E6E1F2] bg-white p-8 text-center shadow-sm" role="alert">
              <h1 className="text-xl font-extrabold">الرابط غير صحيح</h1>
              <p className="mt-3 text-sm leading-7 text-[#707070]">لو الإيميل قديم، اطلب رابط جديد من EduNasser.</p>
            </div>
          ) : loadError || !profile ? (
            <div className="rounded-3xl border border-[#E6E1F2] bg-white p-8 text-center shadow-sm" role="alert">
              <h1 className="text-xl font-extrabold">مقدرناش نحمّل البيانات</h1>
              <p className="mt-3 text-sm leading-7 text-[#707070]">الاتصال وقف قبل ما الصفحة تفتح. جرّب تاني.</p>
              <button type="button" onClick={() => setReloadKey((key) => key + 1)} className="mt-5 rounded-xl bg-[#5B3FD0] px-5 py-3 text-sm font-bold text-white hover:bg-[#4729b0]">
                جرّب تاني
              </button>
            </div>
          ) : (
            <form
              className="rounded-3xl border border-[#E6E1F2] bg-white p-5 shadow-sm sm:p-8"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
              noValidate
            >
              <p className="text-sm font-bold text-[#5B3FD0]">منحة EduNasser</p>
              <h1 className="mt-2 text-2xl font-extrabold leading-snug">بيانات شهادة اللغة الإنجليزية</h1>
              <p className="mt-3 text-sm leading-7 text-[#707070]">
                أهلًا {profile.full_name}. طلبك رقم <bdi dir="ltr" className="font-bold text-[#2C2C2C]">{profile.reference_number}</bdi> انتقل للمرحلة التالية. كمّل البيانات دي عشان نكمّل المراجعة.
              </p>

              {saved && (
                <p role="status" className="mt-5 flex items-start gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold leading-7 text-emerald-800">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>اتحفظت بياناتك. تقدر ترجع لنفس الرابط وتعدّلها في أي وقت.</span>
                </p>
              )}

              <fieldset className="mt-6">
                <legend className="text-sm font-extrabold">نوع الشهادة</legend>
                <div className="mt-3 grid gap-2" role="radiogroup" aria-describedby={errors.certificate ? `${formId}-certificate-error` : undefined}>
                  {CERTIFICATES.map((item) => {
                    const selected = certificate === item.id;
                    return (
                      <label key={item.id} className={`flex cursor-pointer items-center gap-3 rounded-2xl border-2 px-4 py-3 ${selected ? 'border-[#5B3FD0] bg-[#5B3FD0]/5' : 'border-gray-200 bg-white'}`}>
                        <input
                          id={item.id === 'moi' ? `${formId}-certificate` : undefined}
                          type="radio"
                          name="certificate"
                          value={item.id}
                          checked={selected}
                          onChange={() => {
                            setCertificate(item.id);
                            setSaved(false);
                            setErrors((current) => ({ ...current, certificate: undefined }));
                          }}
                          className="h-4 w-4 accent-[#5B3FD0]"
                        />
                        <span className="text-sm font-bold">
                          {item.latin ? <bdi dir="ltr">{item.title}</bdi> : item.title}
                          {item.hint ? <span className="ms-2 font-semibold text-[#707070]" dir="ltr">{item.hint}</span> : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {errors.certificate && <p id={`${formId}-certificate-error`} className="mt-2 text-sm font-semibold text-red-700">{errors.certificate}</p>}
              </fieldset>

              {skipsDetails && (
                <p className="mt-4 rounded-2xl bg-[#F6F4FB] px-4 py-3 text-sm leading-7 text-[#3F3F46]">
                  {certificate === 'none'
                    ? 'معنديش شهادة لغة إنجليزي. مش محتاجين المستوى ولا تاريخ الإصدار.'
                    : 'في الحالة دي مش محتاجين المستوى ولا تاريخ الإصدار.'}
                </p>
              )}

              {certificate === 'other' && (
                <div className="mt-5">
                  <label htmlFor={`${formId}-otherName`} className="text-sm font-extrabold">اسم الشهادة</label>
                  <input
                    id={`${formId}-otherName`}
                    value={otherName}
                    dir="auto"
                    maxLength={120}
                    onChange={(event) => {
                      setOtherName(event.target.value);
                      setSaved(false);
                      setErrors((current) => ({ ...current, otherName: undefined }));
                    }}
                    aria-invalid={Boolean(errors.otherName)}
                    aria-describedby={errors.otherName ? `${formId}-otherName-error` : undefined}
                    placeholder="مثال: Cambridge"
                    className="mt-2 w-full rounded-2xl border-2 border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#5B3FD0]"
                  />
                  {errors.otherName && <p id={`${formId}-otherName-error`} className="mt-2 text-sm font-semibold text-red-700">{errors.otherName}</p>}
                </div>
              )}

              {needsDetails && (
                <>
                  <fieldset className="mt-6">
                    <legend className="text-sm font-extrabold">المستوى</legend>
                    <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-describedby={errors.score ? `${formId}-score-error` : undefined}>
                      {SCORE_PRESETS.map((level) => {
                        const selected = scoreChoice === level;
                        return (
                          <label key={level} className={`cursor-pointer rounded-xl border-2 px-4 py-2.5 text-sm font-bold focus-within:ring-2 focus-within:ring-[#5B3FD0] ${selected ? 'border-[#5B3FD0] bg-[#5B3FD0] text-white' : 'border-gray-200 bg-white'}`}>
                            <input
                              id={level === 'A1' && scoreChoice !== 'custom' ? `${formId}-score` : undefined}
                              type="radio"
                              name="score"
                              value={level}
                              checked={selected}
                              onChange={() => {
                                setScoreChoice(level);
                                setSaved(false);
                                setErrors((current) => ({ ...current, score: undefined }));
                              }}
                              className="sr-only"
                            />
                            <bdi dir="ltr">{level}</bdi>
                          </label>
                        );
                      })}
                      <label className={`cursor-pointer rounded-xl border-2 px-4 py-2.5 text-sm font-bold focus-within:ring-2 focus-within:ring-[#5B3FD0] ${scoreChoice === 'custom' ? 'border-[#5B3FD0] bg-[#5B3FD0]/5 text-[#5B3FD0]' : 'border-gray-200 bg-white'}`}>
                        <input
                          type="radio"
                          name="score"
                          value="custom"
                          checked={scoreChoice === 'custom'}
                          onChange={() => {
                            setScoreChoice('custom');
                            setSaved(false);
                            setErrors((current) => ({ ...current, score: undefined }));
                          }}
                          className="sr-only"
                        />
                        اكتب مستوايا
                      </label>
                    </div>
                    {scoreChoice === 'custom' && (
                      <input
                        id={`${formId}-score`}
                        value={customScore}
                        dir="auto"
                        maxLength={40}
                        onChange={(event) => {
                          setCustomScore(event.target.value);
                          setSaved(false);
                          setErrors((current) => ({ ...current, score: undefined }));
                        }}
                        aria-invalid={Boolean(errors.score)}
                        placeholder="مثال: 6.5"
                        className="mt-3 w-full rounded-2xl border-2 border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#5B3FD0]"
                      />
                    )}
                    {errors.score && <p id={`${formId}-score-error`} className="mt-2 text-sm font-semibold text-red-700">{errors.score}</p>}
                  </fieldset>

                  <div className="mt-6">
                    <label htmlFor={`${formId}-issuedOn`} className="text-sm font-extrabold">تاريخ إصدار الشهادة</label>
                    <input
                      id={`${formId}-issuedOn`}
                      type="date"
                      dir="ltr"
                      value={issuedOn}
                      onChange={(event) => {
                        setIssuedOn(event.target.value);
                        setSaved(false);
                        setErrors((current) => ({ ...current, issuedOn: undefined }));
                      }}
                      aria-invalid={Boolean(errors.issuedOn)}
                      aria-describedby={errors.issuedOn ? `${formId}-issuedOn-error` : undefined}
                      className="mt-2 w-full rounded-2xl border-2 border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#5B3FD0]"
                    />
                    {errors.issuedOn && <p id={`${formId}-issuedOn-error`} className="mt-2 text-sm font-semibold text-red-700">{errors.issuedOn}</p>}
                  </div>
                </>
              )}

              {saveError && <p role="alert" className="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold leading-7 text-red-800">{saveError}</p>}

              <button
                type="submit"
                disabled={saving}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#5B3FD0] px-5 py-3.5 text-base font-bold text-white hover:bg-[#4729b0] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'بنحفظ البيانات' : profile.submitted_at ? 'احفظ التعديل' : 'احفظ البيانات'}
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}
